/* global __CLIENT__*/
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Map, List } from 'immutable';
import { createSelector } from 'reselect';
import { ElementActions } from '../actions/element';
import InlineEditor from './types/InlineEditor';
import { getComponent, getValidation } from './types/Components';
import { InlineEditorActions } from '../actions/inlineEditor';
import { validateAndDispatch } from '../helpers/form';
import { currentProjectIdSelector } from '../reducers/projects';
import { dependencyTypeIdSelector, wbsRelTypeIdSelector } from '../reducers/relationshipTypes';
import {
  elementsSelector,
  elementsRelationshipsSelector,
  relationshipsSelector,
  relationshipTypesSelector,
} from './ElementRelationships';

class DefaultElementProperties extends PureComponent {
  static propTypes = {
    element: PropTypes.instanceOf(Map),
    elementType: PropTypes.object,
    relationships: PropTypes.object,
    providesToConsumesFrom: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    userMode: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.editSubmit = this.editSubmit.bind(this);
  }

  async editSubmit(elementProp, typeOptions, component) {
    const { dispatch, element, currentProjectId } = this.props;
    const propKey = Object.keys(elementProp)[0];
    const formUid = `element-${element.get('id')}-${propKey}`;

    // Each component can define their own editSubmit function
    if (component && component.editSubmit) {
      await component.editSubmit(elementProp, dispatch, currentProjectId,
                element.get('id'), typeOptions);
      return dispatch(InlineEditorActions.unsetInlineEditState(formUid));
    }

    // Default processing for other components
    const currentElementId = element.get('id');
    const options = {
      props: elementProp,
    };

    const action = ElementActions.updateProperty(currentElementId, options);
    const validation = {};

    await validateAndDispatch(dispatch, action, validation, {
      field: elementProp.propVal,
    });

    return dispatch(InlineEditorActions.unsetInlineEditState(formUid));
  }

  fieldSet = (k, val, index) => {
    let propertyTypeEditor;
    const { element, providesToConsumesFrom, userMode, wbsRelTypeId } = this.props;
    const elementId = element.get('id');
    const uid = `element-${elementId}-${k}`;

    if (val && typeof val.has === 'function' && val.has('_type')) {
      if (val.get('_display') === 'false' || val.get('_display') === false) {
        return <div key={k} />;
      }
      let fieldValue = this.props.element.hasIn(['props', k])
        ? this.props.element.getIn(['props', k], '')
        : null;
      const customHelpText = val.has('_helptext')
        ? val.get('_helptext')
        : null;
      const typeOptions = val.has('_typeOptions') && val.get('_typeOptions')
        ? val.get('_typeOptions').toJSON()
        : null;
      const propType = val.get('_type');
      const TypeComponent = getComponent(propType);
      const typeValidation = getValidation(propType);
      const onSave = this.editSubmit;

      propertyTypeEditor = TypeComponent
        ? <InlineEditor
          key={k}
          component={TypeComponent}
          propertyKey={k}
          value={fieldValue}
          validation={typeValidation}
          form={uid}
          inlineTypeOptions={typeOptions}
          customHelpText={customHelpText}
          onSave={onSave}
          currentElementId={elementId}
          fieldIndex={index}
          userMode={userMode}
          providesToConsumesFrom={providesToConsumesFrom}
          wbsRelTypeId={wbsRelTypeId}
        />
        : null;
    }

    return (
      <div key={`fg-${k}`} className="form-group row mb-2">
        <label className="col-sm-3 col-form-label" style={{ padding: 0 }}>{k}</label>
        <div className="col-sm-9">{propertyTypeEditor}</div>
      </div>
    );
  };

  buildProperties = () => {
    const { element } = this.props;
    const properties = element.getIn(['type', 'props']);
    if (!properties || !properties.size) return <span />;

    let index = 1;
    return (
      <div>
        {properties
          .sortBy(p => p.get('_order'))
          .map((prop, key) => this.fieldSet(key, prop, (index++)))
          .toArray()}
      </div>
    );
  };

  render() {
    const elementProps = this.buildProperties();
    return (
      <div
        style={{ marginTop: 20 }}
        className="element-props"
      >
        {elementProps}
      </div>
    );
  }
}

const currentElementIdSelector = (state, props) => props.element.get('id');

const elementSelector = (state, props) => {
  const currentElementId = currentElementIdSelector(state, props);
  return currentElementId
    ? state.elements.getIn(['elements', currentElementId])
    : null;
};

const elementTypeSelector = (state, props) => {
  const currentElementId = currentElementIdSelector(state, props);

  const testing = state.elements.getIn([
    'elements',
    currentElementId,
    'type_id',
  ]);
  const current = testing
    ? state.elementTypes.getIn(['elementTypes', testing.toString()])
    : null;

  return currentElementId && current ? current : null;
};

const providesToConsumesFromSelector = (state, props) => {
  const consumesFrom = elementsRelationshipsSelector(state).getIn(
    [currentElementIdSelector(state, props), 'consumesFrom'],
    List(),
  );
  const providesTo = elementsRelationshipsSelector(state).getIn(
    [currentElementIdSelector(state, props), 'providesTo'],
    List(),
  );

  return Map({
    consumesFrom,
    providesTo,
  });
};

const inlineEditorSelector = (state) => state.inlineEditors.get('editors');

const ElementPropertiesContainerSelector = createSelector(
  [
    currentProjectIdSelector,
    dependencyTypeIdSelector,
    currentElementIdSelector,
    elementSelector,
    elementTypeSelector,
    inlineEditorSelector,
    elementsSelector,
    providesToConsumesFromSelector,
    relationshipsSelector,
    relationshipTypesSelector,
    wbsRelTypeIdSelector,
  ],
  (
    currentProjectId,
    dependencyTypeId,
    currentElementId,
    element,
    elementType,
    editors,
    elements,
    providesToConsumesFrom,
    relationships,
    relationshipTypes,
    wbsRelTypeId,
  ) => ({
    currentProjectId,
    dependencyTypeId,
    relationships,
    currentElementId,
    elementType,
    providesToConsumesFrom,
    element: element ? element.set('type', elementType) : null,
    editors,
    wbsRelTypeId,
  })
);

@connect(ElementPropertiesContainerSelector)
export default class ElementPropertiesContainer extends PureComponent {
  static propTypes = {
    dispatch: PropTypes.func,
    element: PropTypes.instanceOf(Map),
    relationships: PropTypes.object,
  };

  render() {
    if (!this.props.element) {
      return <div />;
    }
    return (
      <div>
        <DefaultElementProperties {...this.props} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ElementProperties.js